import _ from 'lodash'

import { ApiHelper } from '../../../helpers/apihelper'
import * as Constants from '../../../helpers/constants'
import { SEED_TRIALS, isExpiringSoon } from './trial_data'

/* Data Pulse free-trial data-access seam.
 *
 * Backend (sfcloud BFF) exposes list / metrics / status / extend / expire / re_enable
 * under api/v1/{client}/data_pulse/free_trial/<op>. See
 * docs/data_pulse_free_trial_api_gaps.md for the contract + remaining asks (org_id,
 * user_type on list rows).
 *
 * Set USE_SEED_DATA = true to fall back to the local seed dataset (offline dev).
 */
export const USE_SEED_DATA = false

const isSuccess = (response) => response && response.status === Constants.CO_API.SUCCESS

const matchesSearch = (trial, q) =>
  !q || trial.name.toLowerCase().includes(q) || trial.email.toLowerCase().includes(q) || trial.org.toLowerCase().includes(q)

// Map a backend list row → the shape the table/components consume.
const mapRow = (r) => ({
  co_user_id: r.co_user_id,
  name: r.name,
  email: r.email,
  org: r.org || null,
  org_id: r.org_id || null,
  status: r.status,
  startDate: r.started_on,
  endDate: r.expires_on,
  durationDays: r.duration_days,
  extendedDays: r.extended_days,
  daysRemaining: r.days_remaining,
  offeredBy: (r.offered_by && r.offered_by.name) || 'System',
  offeredDate: r.offered_on,
})

// ---- List (server-side: skip/limit/search/status) → { rows, count } ---------
export const listTrials = (client, params = {}, signal = null) => {
  if (USE_SEED_DATA) {
    const q = (params.search || '').trim().toLowerCase()
    const status = params.status && params.status !== 'all' ? params.status : 'all'
    const all = SEED_TRIALS
      .filter((t) => t.product === (params.product || 'data_pulse'))
      .filter((t) => status === 'all' || t.status === status)
      .filter((t) => matchesSearch(t, q))
    const skip = params.skip || 0
    const limit = params.limit || all.length
    return Promise.resolve({ rows: all.slice(skip, skip + limit).map((t) => ({ ...t })), count: all.length })
  }
  const body = _.pick(params, ApiHelper(client, 'TRIAL_LIST').API_BODY)
  return ApiHelper(client, 'TRIAL_LIST').API(client, body, signal)
    .then((response) => {
      if (!isSuccess(response)) return { rows: [], count: 0 }
      const data = response.data || {}
      return { rows: (data.rows || []).map(mapRow), count: data.count || 0 }
    })
}

// ---- Metrics → { offered, active, expiringSoon, expired } --------------------
export const fetchMetrics = (client, params = {}, signal = null) => {
  if (USE_SEED_DATA) {
    const all = SEED_TRIALS.filter((t) => t.product === (params.product || 'data_pulse'))
    return Promise.resolve({
      offered: all.length,
      active: all.filter((t) => t.status === 'active').length,
      expiringSoon: all.filter((t) => isExpiringSoon(t)).length,
      expired: all.filter((t) => t.status === 'expired').length,
    })
  }
  const body = _.pick(params, ApiHelper(client, 'TRIAL_METRICS').API_BODY)
  return ApiHelper(client, 'TRIAL_METRICS').API(client, body, signal)
    .then((response) => {
      const totals = (isSuccess(response) && response.data && response.data.totals) || {}
      return {
        offered: totals.offered || 0,
        active: totals.active || 0,
        expiringSoon: totals.expiring_soon || 0,
        expired: totals.expired || 0,
      }
    })
}

const runAction = (client, helperKey, params) => {
  if (USE_SEED_DATA) return Promise.resolve({})
  const body = _.pick(params, ApiHelper(client, helperKey).API_BODY)
  return ApiHelper(client, helperKey).API(client, body)
    .then((response) => {
      if (!isSuccess(response)) throw new Error((response && response.message) || 'Request failed')
      return response.data || {}
    })
}

// coUserId = target user; operatorId = logged-in CRM operator (recorded in the audit).
export const extendTrial = (client, { coUserId, operatorId, days, reason }) =>
  runAction(client, 'TRIAL_EXTEND', { co_user_id: coUserId, user_id: operatorId, days, reason })

export const expireTrial = (client, { coUserId, operatorId, reason }) =>
  runAction(client, 'TRIAL_EXPIRE', { co_user_id: coUserId, user_id: operatorId, reason })

export const reenableTrial = (client, { coUserId, operatorId, days, reason }) =>
  runAction(client, 'TRIAL_REENABLE', { co_user_id: coUserId, user_id: operatorId, days, reason })
